import { useEffect, useRef } from "react";
import SplideArrows from "@components/Slider/components/SplideArrows";
import {
  Options,
  Splide,
  SplideSlide,
  SplideTrack,
} from "@greglaisph/react-splide";
import { ArticleMainMediaFactory } from "@routes/Article/components/ArticleMainMedia/utils/helpers";
import { ArticleMainMediaProps } from "@routes/Article/components/ArticleMainMedia/utils/types";
import { cn, getImageCaptionCreditFromSummary } from "@util/helpers";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "@greglaisph/react-splide/css";

const ArticleMainMedia = ({
  media,
  className,
  captionClassName,
}: ArticleMainMediaProps): React.ReactElement => {
  const options: Options = {
    perMove: 1,
    pagination: false,
    arrows: media?.length > 1,
    snap: true,
    focus: 0,
    type: media?.length > 1 ? "loop" : "slide",
    speed: 500,
    drag: media?.length > 1,
    classes: {
      // Add classes for pagination.
      page: `splide__pagination__page aria-selected:scale-[1.25] aria-selected:opacity-100 aria-selected: bg-gray-650 m-[0.25rem]`, // each button
    },
  };

  const optionsForCaption: Options = {
    perMove: 1,
    pagination: false,
    arrows: false,
    snap: true,
    focus: 0,
    type: media?.length > 1 ? "loop" : "slide",
    drag: false,
    speed: 500,
  };

  const thumbnailsRef = useRef<Splide>(null);
  const captionRef = useRef<Splide>(null);

  useEffect(() => {
    if (
      thumbnailsRef.current &&
      captionRef.current &&
      captionRef.current.splide &&
      thumbnailsRef.current.splide
    ) {
      captionRef.current.sync(thumbnailsRef.current.splide);
    }
  }, [thumbnailsRef, captionRef]);

  if (media.length === 0) return <></>;

  return (
    <div
      className={cn(
        "no-print -mx-4 aspect-3x2 w-svw md:mx-0 md:w-full",
        className
      )}
      data-testid="article-main-media-component"
    >
      <Splide options={options} hasTrack={false} ref={thumbnailsRef}>
        <ul className="splide__pagination"></ul>

        <SplideArrows
          className="absolute flex h-full w-full items-center justify-between"
          nextArrowClassName={cn(
            "!-right-12 !border-gray-175 hover:!border-gray-850"
          )}
          prevArrowClassName={cn(
            "!-left-12 !border-gray-175 hover:!border-gray-850"
          )}
        />

        <SplideTrack>
          {media.map((media, index) => {
            const type = media.type || "";

            const Layout = ArticleMainMediaFactory(type);

            return (
              <SplideSlide
                key={`media-${media.id}-${index}`}
                className="flex w-full items-center justify-center"
              >
                <Layout
                  media={media}
                  className="aspect-3x2"
                  withBlurBackground
                />
              </SplideSlide>
            );
          })}
        </SplideTrack>
      </Splide>

      <Splide options={optionsForCaption} hasTrack={false} ref={captionRef}>
        <SplideTrack>
          {media.map(({ caption, credit, id }) => {
            const summary = [
              {
                key: "caption",
                value: caption || "",
              },
              {
                key: "credit",
                value: credit || "",
              },
            ];

            return (
              <SplideSlide
                key={`caption-media-container-${id}`}
                className={cn(
                  "px-3 py-2 font-poppins text-xs text-gray-850 md:px-8",
                  captionClassName
                )}
              >
                <div>{getImageCaptionCreditFromSummary(summary)}</div>
              </SplideSlide>
            );
          })}
        </SplideTrack>
      </Splide>
    </div>
  );
};

export default ArticleMainMedia;
